import * as React from "react";
import styled from "styled-components";
import { PageTitle, PageSubtitle } from "../../typo";
import BackButton from "../BackButton";
import { select, useSelect } from "@hubper/theme";
import mergeStyles from "../../util/mergeStyles";

const Base = styled.div<{ scroll?: boolean }>`
  display: flex;
  flex-direction: column;
  height: ${p => !p.scroll && "100%"};
  min-height: ${p => p.scroll && "100%"};
  padding: ${select.space(6)};
  box-sizing: border-box;
`;

const PageTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const PageTopLeft = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  justify-content: flex-start;
  margin-right: ${select.space(4)};
`;

const PageTopRightBase = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-top: ${select.space(2)};

  & > *:not(:last-child) {
    margin-right: ${select.space(0)};
  }
`;

type PageTopRightProps = {
  children: any;
  style?: React.CSSProperties;
  className?: string;
};

const PageTopRight = React.forwardRef(function PageTopRight(
  { children, style, className }: PageTopRightProps,
  ref: any
) {
  return (
    <PageTopRightBase ref={ref} style={style} className={className}>
      {children}
    </PageTopRightBase>
  );
});

type PageBodyProps = {
  children: any;
  style?: React.CSSProperties;
  className?: string;
};

const PageBodyBase = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  flex-grow: 1;
  &:not(:first-child) {
    margin-top: ${select.space(5)};
  }
`;

const PageBody = React.forwardRef(function PageBody(
  { children, style, className }: PageBodyProps,
  ref: any
) {
  return (
    <PageBodyBase ref={ref} style={style} className={className}>
      {children}
    </PageBodyBase>
  );
});

const renderChild = (children: any[], childToRender: React.ReactNode) => {
  return children.find(child => child.type === childToRender);
};

type Props = {
  style?: React.CSSProperties;
  className?: string;
  title?: string;
  subtitle?: string;
  backButton?: boolean;
  backButtonCallback?: () => void;
  children: any;
  animated?: boolean;
  scroll?: boolean;
};

const Page = React.forwardRef(function Page(
  {
    style,
    className,
    title,
    subtitle,
    backButton,
    backButtonCallback,
    children,
    animated,
    scroll
  }: Props,
  ref: any
) {
  const childrenArray = React.Children.toArray(children);
  const { space } = useSelect();

  return (
    <Base
      scroll={scroll}
      ref={ref}
      style={mergeStyles(
        style,
        animated
          ? {
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              willChange: "transform"
            }
          : {}
      )}
      className={className}
    >
      {(title || subtitle || renderChild(childrenArray, PageTopRight)) && (
        <PageTop>
          <PageTopLeft>
            {title && (
              <PageTitle style={{ marginBottom: space(0) }}>{title}</PageTitle>
            )}
            {backButton && backButtonCallback && subtitle && (
              <BackButton onClick={backButtonCallback} label={subtitle} />
            )}
            {subtitle && !backButton && <PageSubtitle>{subtitle}</PageSubtitle>}
          </PageTopLeft>
          {renderChild(childrenArray, PageTopRight)}
        </PageTop>
      )}
      {renderChild(childrenArray, PageBody)}
    </Base>
  );
}) as React.ComponentType<Props> & {
  TopRight?: React.ComponentType<PageTopRightProps>;
  Body?: React.ComponentType<PageBodyProps>;
};

Page.TopRight = PageTopRight;
Page.Body = PageBody;

export default Page;
